import { Box, Stack } from '@mui/material'
import { FeatureFlagIdRequestSchema } from 'c1gen/features'
import { TaskV1TaskActionsServiceApprove, TaskV1TaskActionsServiceApproveWithStepUp, TaskV1TaskActionsServiceDeny, TaskV1TaskActionsServiceMarkProvisionComplete } from 'c1gen/permissions'
import { C1Chip } from 'c1ui/components/C1Chip'
import { ExternalLink } from 'components/core/links/external-link'
import { AccountDeletedPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/AccountDeletedPrimaryAction'
import { ApprovePrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/ApprovePrimaryAction'
import { ApproveWithStepPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/ApproveWithStepPrimaryAction'
import { DenyPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/DenyPrimaryAction'
import { SelfApprovalNotAllowedPrimaryAction } from 'components/core/task-list-v2/task-list-v2-components/SelfApprovalNotAllowedPrimaryAction'
import { HasCapability, useFeatureChecker, useHasAnyPermission, useHasBuiltinRole } from 'components/no-ui/capability'
import { ApprovalOneOfKind, ProvisionOneOfKind } from 'components/pages/policy/type'
import { isSelfActionAllowed } from 'components/pages/task/details'
import { linkLabel } from 'components/pages/task/details/content/TaskCurrentStep'
import { TaskStatusLabel } from 'components/pages/task/details/content/TaskStatusLabel'
import { canActOn } from 'components/pages/task/state'
import { useGetExternalTicket } from 'hooks/useGetExternalTicket'
import { TaskValue } from 'lib/actions/task'
import { RoleID } from 'lib/constants/system'
import { UserContext } from 'lib/context/app'
import { TaskActionType, TaskProcessingType } from 'pbts/c1/api/task/v1/task'
import { FC, useContext, useEffect, useMemo } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { setKeyValue } from 'redux_toolkit/slices/keyValueSlice'
import { MarkProvisionCompletePrimaryAction } from './MarkProvisionCompletePrimaryAction'

export type TaskPrimaryActionsV2Variant = 'default' | 'icon-button' | 'contained'

type TaskPrimaryActionsV2Props = {
    task: TaskValue
    hideDenyButton?: boolean
    CustomButton?: React.ReactElement
    variant?: TaskPrimaryActionsV2Variant
    neverShowTaskStatusLabel?: boolean
}

const getPositiveOutcomeText = (task: TaskValue, intl: IntlShape): string => {
    switch (task.type?.taskType.oneofKind) {
        case 'certify':
            return intl.formatMessage({
                id: 'components_task_outcome_certified',
                defaultMessage: 'Certified',
            })
        case 'grant':
            return intl.formatMessage({
                id: 'components_task_outcome_granted',
                defaultMessage: 'Granted',
            })
        case 'revoke':
            return intl.formatMessage({
                id: 'components_task_outcome_revoked',
                defaultMessage: 'Confirmed',
            })
        default:
            return intl.formatMessage({
                id: 'components_task_outcome_positive_generic',
                defaultMessage: 'Approved',
            })
    }
}

const getNegativeOutcomeText = (task: TaskValue, intl: IntlShape): string => {
    switch (task.type?.taskType.oneofKind) {
        case 'certify':
            return intl.formatMessage({
                id: 'components_task_outcome_decertified',
                defaultMessage: 'Denied',
            })
        case 'revoke':
            return intl.formatMessage({
                id: 'components_task_outcome_declined',
                defaultMessage: 'Declined',
            })
        case 'grant':
            return intl.formatMessage({
                id: 'components_task_outcome_denied',
                defaultMessage: 'Denied',
            })
        default:
            return intl.formatMessage({
                id: 'components_task_outcome_negative_generic',
                defaultMessage: 'Denied',
            })
    }
}

const ExternalTicketChip: FC<{ task: TaskValue }> = ({ task }) => {
    const [isLoadingExternalTicket, errorExternalTicket, externalTicket] = useGetExternalTicket({ task })

    let label = <FormattedMessage id="task_status_processing_waiting_for_helpdesk_text_0" defaultMessage="Waiting: External ticketing" />

    if (!isLoadingExternalTicket && !errorExternalTicket && !!externalTicket?.externalTicket?.externalTicketUrl) {
        const link = linkLabel(externalTicket.externalTicket.externalTicketUrl)

        if (link === externalTicket.externalTicket.externalTicketUrl) {
            label = <FormattedMessage
                id="task_status_processing_waiting_for_helpdesk_text_1"
                defaultMessage="Waiting: {link}"
                values={{
                    link: <ExternalLink href={externalTicket.externalTicket.externalTicketUrl}>
                        <FormattedMessage id="task_status_processing_waiting_for_helpdesk_text_3" defaultMessage="External ticketing" />
                    </ExternalLink>,
                }}
            />
        } else {
            label = <FormattedMessage
                id="task_status_processing_waiting_for_helpdesk_text_3"
                defaultMessage="Waiting: {link}"
                values={{
                    link: <ExternalLink href={externalTicket.externalTicket.externalTicketUrl}>
                        {link}
                    </ExternalLink>,
                }}
            />
        }
    }

    return <C1Chip
        color="info"
        label={label}
    />
}

export const TaskPrimaryActionsV2: FC<TaskPrimaryActionsV2Props> = (
    { task, hideDenyButton = false, CustomButton, neverShowTaskStatusLabel = false, variant = 'default' },
) => {
    const hasRequestSchemaFeature = useFeatureChecker(FeatureFlagIdRequestSchema)
    const userContext = useContext(UserContext)
    const userId = userContext.user.id

    const selfApprovalNotAllowed = useMemo(
        () => task.userId === userId && !isSelfActionAllowed(task) && !!task?.stepApproverIds?.includes(userId),
        [task, userId],
    )
    const taskCannotTakeAction = useMemo(
        () =>
            !canActOn(task, TaskActionType.APPROVE) &&
            !canActOn(task, TaskActionType.APPROVE_WITH_STEP_UP) &&
            !canActOn(task, TaskActionType.DENY) &&
            !canActOn(task, TaskActionType.PROVISION_COMPLETE),
        [task],
    )
    const dispatch = useDispatch()
    const optimisticState = useSelector<Record<string, string>, string | undefined>(
        (state) => state.keyValues[`optimistic-${task.id}`],
    )
    const intl = useIntl()

    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const userCanTakeAction = useHasAnyPermission([
        TaskV1TaskActionsServiceApproveWithStepUp,
        TaskV1TaskActionsServiceApprove,
        TaskV1TaskActionsServiceDeny,
        TaskV1TaskActionsServiceMarkProvisionComplete,
    ])

    useEffect(() => {
        // Only clear optimistic state if task can no longer take action AND is not processing
        // This prevents clearing the optimistic "Certified"/"Denied" state when task enters processing state
        const isProcessing = task.processing === TaskProcessingType.PROCESSING || task.status === 'processing'
        optimisticState &&
            taskCannotTakeAction &&
            !isProcessing &&
            dispatch(setKeyValue({ key: `optimistic-${task.id}`, value: undefined }))
    }, [dispatch, optimisticState, task.id, taskCannotTakeAction, task.processing, task.status])

    if (optimisticState) {
        switch (optimisticState) {
            case 'approve':
                return <C1Chip
                    color="success"
                    sx={{ mr: 1 }}
                    label={getPositiveOutcomeText(task, intl)}
                />
            case 'deny':
                return <C1Chip
                    color="error"
                    sx={{ mr: 1 }}
                    label={getNegativeOutcomeText(task, intl)}
                />
            // no default
        }
    }

    if (task.processing === TaskProcessingType.PROCESSING || task.status === 'processing') {
        // Typically this means the user has just clicked the button to submit the task, so we should say 'Submitted'.
        // But tasks also get into this state if they are waiting for a webhook to complete. So we need to check
        // for that.

        if (
            task.policy?.current?.instance.oneofKind === 'approval' &&
            task.policy?.current?.instance.approval?.approval?.typ.oneofKind === ApprovalOneOfKind.WEBHOOK
        ) {
            return <TaskStatusLabel task={task} />
        }

        if (
            task.policy?.current?.instance.oneofKind === 'provision' &&
            task.policy?.current?.instance.provision.provision?.provisionPolicy?.typ.oneofKind === ProvisionOneOfKind.EXTERNAL_TICKET
        ) {
            return <Box mr={1}><ExternalTicketChip task={task} /></Box>
        }

        if (task.policy?.current?.instance.oneofKind === 'form' && hasRequestSchemaFeature) {
            return <C1Chip
                color="warning"
                label={<FormattedMessage id="components_task_status_requires_additional_information" defaultMessage="Requires information" />}
            />
        }

        return <C1Chip
            color="info"
            sx={{ mr: 1 }}
            label={<FormattedMessage id="task_status_processing_submitted_text" defaultMessage="Submitted" />}
        />
    }

    if (task.appUser?.deletedAt) {
        return <AccountDeletedPrimaryAction task={task} variant={variant} />
    }

    if (selfApprovalNotAllowed) {
        return <SelfApprovalNotAllowedPrimaryAction task={task} variant={variant} />
    }

    if ((isReadOnlySuperAdmin && !userCanTakeAction) || (!neverShowTaskStatusLabel && taskCannotTakeAction)) {
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mr: 1 }}>
                <TaskStatusLabel task={task} />
            </Stack>
        )
    }

    return (
        <Stack direction="row" spacing={1}>
            {canActOn(task, TaskActionType.APPROVE_WITH_STEP_UP) ? (
                <HasCapability permission={TaskV1TaskActionsServiceApproveWithStepUp}>
                    <ApproveWithStepPrimaryAction task={task} variant={variant} />
                </HasCapability>
            ) : (
                <HasCapability permission={TaskV1TaskActionsServiceApprove}>
                    <ApprovePrimaryAction task={task} variant={variant} />
                </HasCapability>
            )}
            {!hideDenyButton && <HasCapability permission={TaskV1TaskActionsServiceDeny}>
                <DenyPrimaryAction task={task} variant={variant} />
            </HasCapability>}
            <HasCapability permission={TaskV1TaskActionsServiceMarkProvisionComplete}>
                <MarkProvisionCompletePrimaryAction task={task} variant={variant} />
            </HasCapability>
            {CustomButton && <>
                {CustomButton}
            </>}
        </Stack>
    )
}
